import React, { forwardRef } from "react";
import styled from "styled-components";
import { tokens } from "../../public/tokens";
import { CheckboxIndicator, VisuallyHiddenInput } from "./Checkbox";

const { colors, typography, globals, effects } = tokens;

const StyledCard = styled.label(({ $checked = false, $disabled = false }) => ({
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: globals["spacing-3"],
    padding: globals["spacing-3"],
    borderRadius: globals["border-radius"],
    border: `${globals["spacing-px"]} solid ${$checked ? colors.brand.blue["500"] : colors.ui.input}`,
    background: $checked ? colors.brand.blue["50"] : colors.ui.card,
    boxShadow: effects.effects.shadow.default,
    cursor: $disabled ? "not-allowed" : "pointer",
    opacity: $disabled ? 0.5 : 1,
    transition: "all 0.15s ease-in-out",

    "&:focus-within": {
        outline: `${globals["spacing-px"]} solid ${colors.ui.ring}`,
        outlineOffset: globals["spacing-0-5"],
    },
}));

const StyledIcon = styled.i({
    width: globals["spacing-6"],
    flexShrink: 0,
    textAlign: "center",
    fontSize: typography.fontSize["xl-18px"],
    color: colors.text.primary,
});

const StyledLabel = styled.span({
    flex: 1,
    minWidth: 0,
    fontFamily: typography.fontFamilies["albert-sans"],
    fontWeight: typography.fontWeights.semibold,
    fontSize: typography.fontSize["lg-14px"],
    lineHeight: typography.lineHeights["100"],
    color: colors.text.primary,
});

/**
 * Bordered, selectable tile with an icon, a label and a checkbox on the right.
 * @param {string} [props.icon] Font Awesome class, e.g. "fal fa-camera".
 * @param {React.ReactNode} props.label Card text.
 * @param {(event: React.ChangeEvent<HTMLInputElement>) => void} [props.onChange] Change handler.
 * Also: checked, disabled, name, value, id (forwarded to the native checkbox).
 */
const CheckboxCard = forwardRef(function CheckboxCard(
    { icon, label, checked = false, disabled = false, onChange, ...props },
    ref
) {
    return (
        <StyledCard $checked={checked} $disabled={disabled}>
            <VisuallyHiddenInput
                ref={ref}
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={onChange}
                {...props}
            />
            {icon && <StyledIcon className={icon} aria-hidden="true" />}
            <StyledLabel>{label}</StyledLabel>
            <CheckboxIndicator checked={checked} />
        </StyledCard>
    );
});

CheckboxCard.displayName = "CheckboxCard";

export default CheckboxCard;
 